<template>
  <ApplicationLayout>
    <v-container
      fluid
      px-12
      py-6
      v-if="ready"
    >
      <div
        class="
          d-flex
          align-center
        "
      >
        <BackTitle
          :to="{
            name: 'pages',
            query: { focus: page.id }
          }"
          :title="title"
          :subtitle="pageTitle | striptags"
        ></BackTitle>

        <v-spacer></v-spacer>

        <v-btn
          color="primary"
          depressed
          @click="goToFront"
        >
          <v-icon left>input</v-icon>
          View page
        </v-btn>
      </div>

      <v-card
        class="pa-6 mt-6"
      >
        <v-form
          @submit.prevent="submit()"
          ref="form"
          v-model="valid"
        >
          <v-row>
            <v-col
              cols="6"
              class="px-6"
            >
              <v-text-field
                v-model="page.title"
                label="Title"
                :rules="rules.title"
              ></v-text-field>

              <v-text-field
                v-model="page.title_short"
                label="Short title"
              ></v-text-field>

              <v-text-field
                v-model="page.html_title"
                label="HTML title tag"
              ></v-text-field>

              <v-text-field
                v-model="page.uri"
                label="URI"
                :rules="rules.uri"
              ></v-text-field>

              <v-select
                v-model="page.status"
                :items="statuses"
                label="Status"
              ></v-select>

              <v-select
                v-model="page.gender"
                :items="genders"
                label="Gender"
              ></v-select>

              <v-row>
                <v-col cols="6">
                  <v-text-field
                    type="number"
                    min="0"
                    label="Price min"
                    v-model="page.price_min"
                    :rules="rules.price_min"
                  ></v-text-field>
                </v-col>

                <v-col cols="6">
                  <v-text-field
                    type="number"
                    :min="parseInt(page.price_min, 10) + 1 || 0"
                    label="Price max"
                    v-model="page.price_max"
                    @change="page.price_max = page.price_max || null"
                  ></v-text-field>
                </v-col>
              </v-row>

              <Combobox
                v-model="page.categories"
                :select-props="{
                  items: _categories,
                  label: 'Categories',
                  chips: true,
                  deletableChips: true,
                  multiple: true
                }"
                @create-new-item="
                  $store.dispatch('Categories/createAndAdd', { name: $event, categories: page.categories })
                "
              ></Combobox>
            </v-col>

            <v-col
              cols="6"
              class="px-6"
            >
              <TextFieldImage
                label="Banner"
                v-model="page.banner"
                name="pageBanner"
              ></TextFieldImage>

              <TextFieldImage
                label="Banner (small)"
                v-model="page.banner_small"
                name="pageBannerSmall"
              ></TextFieldImage>

              <v-textarea
                v-model="page.meta_description"
                label="Meta description"
              ></v-textarea>

              <v-textarea
                v-model="page.text_top"
                label="Text top (disabled for now)"
                disabled
              ></v-textarea>

              <v-text-field
                v-model="page.title_bottom"
                label="Title bottom"
                :rules="rules.title_bottom"
              ></v-text-field>

              <v-textarea
                v-model="page.text_bottom"
                label="Text bottom"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-row
            justify="end"
          >
            <v-btn
              :disabled="!modified"
              v-if="editMode"
              class="mr-4"
              @click="cancel()"
              depressed
            >Cancel</v-btn>

            <v-btn
              :disabled="!valid || !modified"
              color="primary"
              class="mr-4"
              type="submit"
              depressed
            >Save</v-btn>

            <v-btn
              :disabled="!valid || !modified"
              color="primary"
              @click="saveAndGoBack()"
              depressed
            >Save & Go back</v-btn>
          </v-row>
        </v-form>
      </v-card>

      <ProductsList
        v-if="page.id"
        class="mt-12"
        title="Page products"
        :scope="{ pageId: page.id }"
      ></ProductsList>
    </v-container>
  </ApplicationLayout>
</template>

<script>
import { mapState } from 'vuex'
import ApplicationLayout from '../components/layouts/ApplicationLayout.vue'
import Combobox from '../components/Combobox.vue'
import BackTitle from '../components/BackTitle.vue'
import ProductsList from '../components/ProductsList.vue'
import TextFieldImage from '../components/TextFieldImage.vue'

export default {
  components: {
    ApplicationLayout,
    Combobox,
    BackTitle,
    ProductsList,
    TextFieldImage
  },
  data() {
    return {
      valid: false,
      ready: false,
      pageCopy: null,
      editMode: !!this.$route.params.id,
      rules: {
        title: [v => !!v || 'Title is required'],
        uri: [
          v => !!v || 'URI is required',
          v => /^\w/.test(v) || 'URI is malformed',
          v => /\w$/.test(v) || 'URI is malformed'
        ],
        price_min: [v => !Number.isNaN(parseInt(v, 10)) || 'Price min is required']
      },
      statuses: [
        {
          value: 'disabled',
          text: this.$translate.instant('PAGE_DISABLED')
        },
        {
          value: 'enabled',
          text: this.$translate.instant('PAGE_ENABLED')
        }
      ],
      genders: [
        { value: null, text: 'All' },
        { value: 'male', text: this.$translate.instant('GENDER_MALE') },
        { value: 'female', text: this.$translate.instant('GENDER_FEMALE') }
      ]
    }
  },
  computed: {
    ...mapState('Pages', ['page', 'pageTitle']),
    ...mapState('Categories', ['categories']),
    _categories() {
      return this.categories.map(category => ({
        text: category.name,
        value: category.id
      }))
    },
    modified() {
      return this.pageCopy !== JSON.stringify(this.page)
    },
    title() {
      return this.editMode ? 'Page' : 'Create new page'
    }
  },
  async created() {
    await this.getPage(this.$route.params.id)
    this.$store.dispatch('Categories/getAll')
    this.ready = true
  },
  methods: {
    async getPage(id) {
      await this.$store.dispatch('Pages/get', id)
      this.pageCopy = JSON.stringify(this.page)
    },
    goToFront() {
      window.open(`${process.env.VUE_APP_FRONT_URL}${this.page.uri}.html`, '_blank')
    },
    cancel() {
      this.$store.commit('Pages/setPage', JSON.parse(this.pageCopy))
    },
    async submit() {
      if (!this.$refs.form.validate()) return
      const snackbar = { text: 'Page update succeed' }
      try {
        await this.$store.dispatch('Pages/createOrUpdate')
        if (!this.editMode) {
          this.$router.push({ name: 'page', params: { id: this.page.id } })
          snackbar.text = 'Page creation succeed'
          this.editMode = true
        }
        this.pageCopy = JSON.stringify(this.page)
      } catch (err) {
        snackbar.text = this.editMode ? 'Page update failed' : 'Page creation failed'
        snackbar.color = 'error'
      }
      this.$store.commit('Global/setSnackbar', snackbar)
    },
    async saveAndGoBack() {
      await this.submit()
      this.$router.push({ name: 'pages' })
    }
  },
  async beforeRouteUpdate(to, from, next) {
    if (to.name === from.name && to.params.id !== from.params.id) {
      await this.getPage(to.params.id)
    }
    next()
  },
  beforeRouteLeave(to, from, next) {
    if (from.name !== 'new-page') {
      this.$store.commit('Pages/setPage', null)
      this.$store.commit('Products/setProducts', null)
    }
    next()
  }
}
</script>
